import { FIRST_ARRAY_ELEMENT, ONE, ZERO } from '@/constants/common';
import { downloadElements, downloadNetwork } from '@/redux/export/export.thunks';
import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { modelsDataSelector, modelsIdsSelector } from '@/redux/models/models.selectors';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { DownloadCurrentView } from '@/components/Map/Drawer/ExportDrawer/ExportCompound/DownloadCurrentView/DownloadCurrentView.component';
import { activeOverlaysIdSelector } from '@/redux/overlayBioEntity/overlayBioEntity.selector';
import { useMapInstance } from '@/utils/context/mapInstanceContext';
import { MapFormat } from '@/components/Map/Drawer/ExportDrawer/ExportCompound/MapFormat/MapFormat.component';
import { apiPath } from '@/redux/apiPath';
import { downloadFileFromNewApiPost } from '@/redux/export/export.utils';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { showToast } from '@/utils/showToast';
import { DownloadMap } from '@/components/Map/Drawer/ExportDrawer/ExportCompound/DownloadGraphics/DownloadMap.component';
import { GraphicsMapToggle } from '@/components/Map/Drawer/ExportDrawer/ExportCompound/GraphicsMapToggle';
import { CheckboxItem } from '../CheckboxFilter/CheckboxFilter.types';
import { Annotations } from './Annotations';
import { DownloadElements } from './DownloadElements/DownloadElements';
import { DownloadGraphics } from './DownloadGraphics';
import { DownloadNetwork } from './DownloadNetwork/DownloadNetwork';
import { ExcludedCompartmentPathways } from './ExcludedCompartmentPathways';
import { ELEMENTS_COLUMNS, NETWORK_COLUMNS } from './ExportCompound.constant';
import { ExportContext } from './ExportCompound.context';
import { ImageExportNotice } from './ImageExportNotice';
import { ImageFormat } from './ImageFormat';
import { ImageSize } from './ImageSize';
import { DEFAULT_IMAGE_SIZE } from './ImageSize/ImageSize.constants';
import { ImageSize as ImageSizeType } from './ImageSize/ImageSize.types';
import { IncludedCompartmentPathways } from './IncludedCompartmentPathways';
import { Submap } from './Submap';
import { getDownloadElementsBodyRequest } from './utils/getDownloadElementsBodyRequest';
import { getImageDownloadBodyRequest } from './utils/getImageDownloadBodyRequest';
import { getModelExportZoom } from './utils/getModelExportZoom';
import { getMapDownloadBodyRequest } from './utils/getMapDownloadBodyRequest';
import { getNetworkDownloadBodyRequest } from './utils/getNetworkBodyRequest';

type ExportProps = {
  children: ReactNode;
};

export const Export = ({ children }: ExportProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const modelIds = useAppSelector(modelsIdsSelector);

  const currentModels = useAppSelector(modelsDataSelector);
  const overlays = useAppSelector(activeOverlaysIdSelector);
  const [annotations, setAnnotations] = useState<CheckboxItem[]>([]);
  const [includedCompartmentPathways, setIncludedCompartmentPathways] = useState<CheckboxItem[]>(
    [],
  );
  const [excludedCompartmentPathways, setExcludedCompartmentPathways] = useState<CheckboxItem[]>(
    [],
  );
  const [models, setModels] = useState<CheckboxItem[]>([]);
  const [imageSize, setImageSize] = useState<ImageSizeType>(DEFAULT_IMAGE_SIZE);
  const [imageFormats, setImageFormats] = useState<CheckboxItem[]>([]);
  const [mapFormats, setMapFormats] = useState<CheckboxItem[]>([]);
  const [graphicsMapToggle, setGraphicsMapToggle] = useState<boolean>(true);
  const [isDownloadingMap, setIsDownloadingMap] = useState<boolean>(false);
  const [isDownloadingImage, setIsDownloadingImage] = useState<boolean>(false);

  const handleDownloadElements = useCallback(async () => {
    const body = getDownloadElementsBodyRequest({
      columns: ELEMENTS_COLUMNS,
      modelIds,
      annotations,
      includedCompartmentPathways,
      excludedCompartmentPathways,
    });
    await dispatch(downloadElements(body));
  }, [modelIds, annotations, includedCompartmentPathways, excludedCompartmentPathways, dispatch]);

  const handleDownloadNetwork = useCallback(async () => {
    const data = getNetworkDownloadBodyRequest({
      columns: NETWORK_COLUMNS,
      modelIds,
      annotations,
      includedCompartmentPathways,
      excludedCompartmentPathways,
    });

    await dispatch(downloadNetwork(data));
  }, [modelIds, annotations, includedCompartmentPathways, excludedCompartmentPathways, dispatch]);

  const handleDownloadGraphics = useCallback(async () => {
    const modelId = models?.[FIRST_ARRAY_ELEMENT]?.id;
    const model = currentModels.find(currentModel => currentModel.id === Number(modelId));
    // the id of an image format option is an ImageGeneratorEnum value, see imageHandlersSelector
    const imageFormat = imageFormats?.[FIRST_ARRAY_ELEMENT]?.id;

    if (!model || !imageFormat) {
      return;
    }

    setIsDownloadingImage(true);
    try {
      await downloadFileFromNewApiPost(
        apiPath.downloadImage(model.id),
        getImageDownloadBodyRequest({
          imageFormat,
          model,
          zoom: getModelExportZoom(imageSize.width, model),
          overlayIds: overlays,
          currentView: false,
        }),
        model.name,
      );
    } catch (error) {
      showToast({
        type: 'error',
        message: getErrorMessage({ error, prefix: 'Failed to download the image' }),
      });
    } finally {
      setIsDownloadingImage(false);
    }
  }, [models, imageFormats, currentModels, imageSize.width, overlays]);

  const handleDownloadMap = useCallback(async () => {
    const modelId = models?.[FIRST_ARRAY_ELEMENT]?.id;
    const model = currentModels.find(currentModel => currentModel.id === Number(modelId));
    // the id of a map format option is a ConverterEnum value, see formatsHandlersSelector
    const converter = mapFormats?.[FIRST_ARRAY_ELEMENT]?.id;

    if (!model || !converter) {
      return;
    }

    setIsDownloadingMap(true);
    try {
      await downloadFileFromNewApiPost(
        apiPath.downloadMap(model.id),
        getMapDownloadBodyRequest({ converter, model }),
        model.name,
      );
    } catch (error) {
      showToast({
        type: 'error',
        message: getErrorMessage({ error, prefix: 'Failed to download the map' }),
      });
    } finally {
      setIsDownloadingMap(false);
    }
  }, [models, mapFormats, currentModels]);

  const { mapInstance } = useMapInstance();

  const handleDownloadCurrentView = useCallback(async () => {
    // source: https://openlayers.org/en/latest/examples/export-map.html
    mapInstance?.once('rendercomplete', () => {
      const mapCanvas = document.createElement('canvas');
      const size = mapInstance.getSize();
      if (size) {
        mapCanvas.width = size[ZERO];
        mapCanvas.height = size[ONE];
      }
      const mapContext = mapCanvas.getContext('2d');
      if (!mapContext) {
        return;
      }
      Array.prototype.forEach.call(
        mapInstance.getViewport().querySelectorAll('.ol-layer canvas, canvas.ol-layer'),
        (canvas: HTMLCanvasElement & { parentNode: HTMLElement }) => {
          if (canvas.width > ZERO) {
            const opacity = canvas.parentNode.style.opacity || canvas.style.opacity;
            mapContext.globalAlpha = opacity === '' ? ONE : Number(opacity);
            let matrixValues: number[];
            const MATRIX_CAPTURE_GROUP = 1;
            const { transform } = canvas.style;
            if (transform) {
              // Get the transform parameters from the style's transform matrix
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              matrixValues = transform
                .match(/^matrix\(([^(]*)\)$/)!
                [MATRIX_CAPTURE_GROUP].split(',')
                .map(Number);
            } else {
              matrixValues = [
                parseFloat(canvas.style.width) / canvas.width,
                ZERO,
                ZERO,
                parseFloat(canvas.style.height) / canvas.height,
                ZERO,
                ZERO,
              ];
            }
            // Apply the transform to the export map context
            const [a, b, c, d, e, f] = matrixValues;
            mapContext.setTransform(a, b, c, d, e, f);
            const { backgroundColor } = canvas.parentNode.style;
            if (backgroundColor) {
              mapContext.fillStyle = backgroundColor;
              mapContext.fillRect(ZERO, ZERO, canvas.width, canvas.height);
            }
            mapContext.drawImage(canvas, ZERO, ZERO);
          }
        },
      );
      mapContext.globalAlpha = 1;
      mapContext.setTransform(ONE, ZERO, ZERO, ONE, ZERO, ZERO);

      const link = document.createElement('a');
      link.download = 'map.png';
      link.href = mapCanvas.toDataURL();
      link.click();
    });
    mapInstance?.renderSync();
  }, [mapInstance]);

  const globalContextDataValue = useMemo(
    () => ({
      annotations,
      includedCompartmentPathways,
      excludedCompartmentPathways,
      models,
      imageSize,
      imageFormats,
      mapFormats,
      graphicsMapToggle,
      isDownloadingMap,
      isDownloadingImage,
    }),
    [
      annotations,
      includedCompartmentPathways,
      excludedCompartmentPathways,
      models,
      imageSize,
      imageFormats,
      mapFormats,
      graphicsMapToggle,
      isDownloadingMap,
      isDownloadingImage,
    ],
  );

  const globalContextValue = useMemo(
    () => ({
      setAnnotations,
      setIncludedCompartmentPathways,
      setExcludedCompartmentPathways,
      setModels,
      setImageSize,
      setImageFormats,
      setMapFormats,
      setGraphicsMapToggle,
      handleDownloadElements,
      handleDownloadNetwork,
      handleDownloadGraphics,
      handleDownloadMap,
      handleDownloadCurrentView,
      data: globalContextDataValue,
    }),
    [
      handleDownloadElements,
      handleDownloadNetwork,
      globalContextDataValue,
      handleDownloadGraphics,
      handleDownloadMap,
      handleDownloadCurrentView,
    ],
  );

  return <ExportContext.Provider value={globalContextValue}>{children}</ExportContext.Provider>;
};

Export.Annotations = Annotations;
Export.IncludedCompartmentPathways = IncludedCompartmentPathways;
Export.ExcludedCompartmentPathways = ExcludedCompartmentPathways;
Export.DownloadElements = DownloadElements;
Export.Submap = Submap;
Export.ImageSize = ImageSize;
Export.ImageFormat = ImageFormat;
Export.GraphicsMapToggle = GraphicsMapToggle;
Export.MapFormat = MapFormat;
Export.DownloadNetwork = DownloadNetwork;
Export.DownloadGraphics = DownloadGraphics;
Export.DownloadMap = DownloadMap;
Export.DownloadCurrentView = DownloadCurrentView;
Export.ImageExportNotice = ImageExportNotice;
